import { z } from 'zod'
import type { ResearchEvidence } from '@/lib/tools/research-evidence'

/**
 * Shared contract for the composer autocomplete context — built on the
 * CLIENT from already-mounted providers (brand, usage limits, active
 * surface, chat messages) and enriched on the SERVER with cached
 * account-setup flags. Client-safe: types + zod only, no server imports.
 */

export type PromptSuggestionSurface = 'launch' | 'chat' | 'canvas-chat'

export const PROMPT_SUGGESTION_TRAIL_MAX_ENTRIES = 3
export const PROMPT_SUGGESTION_TRAIL_TEXT_MAX_CHARS = 320
export const PROMPT_SUGGESTION_ARTIFACTS_MAX = 2
export const PROMPT_SUGGESTION_TITLE_MAX_CHARS = 60

export type PromptSuggestionChatTrailEntry = {
  role: 'user' | 'assistant'
  text: string
}

export type PromptSuggestionArtifact = {
  kind: 'email' | 'automation'
  title: string
}

/** Built on the client. Every field optional except `surface` — the
 * suggestion prompt must degrade gracefully when providers are absent. */
export type PromptSuggestionClientContext = {
  surface: PromptSuggestionSurface
  /** Pathname only — querystring is stripped (it churns per selection). */
  path?: string
  /** Salient route entity ids, mirroring the orchestrator's currentUrlParams.
   * Not injected into the prompt verbatim (opaque ids carry no signal) —
   * they derive the `viewing` hint in the context line. */
  automationId?: string
  emailId?: string
  /** Used by the SERVER to key the cached account-status lookup. */
  brandId?: string
  brandName?: string
  plan?: string
  creditsLow?: boolean
  chatTrail?: Array<PromptSuggestionChatTrailEntry>
  /** Titles of emails/automations touched in this chat — the strongest
   * signal for continuing in-flight work by name. */
  artifacts?: Array<PromptSuggestionArtifact>
}

/** Server-derived setup flags merged in by the route (cached, best-effort). */
type PromptSuggestionAccountStatus = {
  hasVerifiedDomain?: boolean
  hasContacts?: boolean
  hasAutomations?: boolean
  activeIntegrationProviders?: Array<string>
}

export type PromptSuggestionContext = PromptSuggestionClientContext &
  PromptSuggestionAccountStatus & { researchEvidence?: Array<ResearchEvidence> }

export const promptSuggestionClientContextSchema = z.object({
  surface: z.enum(['launch', 'chat', 'canvas-chat']),
  path: z.string().max(200).optional(),
  automationId: z.string().max(64).optional(),
  emailId: z.string().max(64).optional(),
  brandId: z.string().max(64).optional(),
  brandName: z.string().max(PROMPT_SUGGESTION_TITLE_MAX_CHARS).optional(),
  plan: z.string().max(40).optional(),
  creditsLow: z.boolean().optional(),
  chatTrail: z
    .array(
      z.object({
        role: z.enum(['user', 'assistant']),
        text: z.string().max(PROMPT_SUGGESTION_TRAIL_TEXT_MAX_CHARS),
      })
    )
    .max(PROMPT_SUGGESTION_TRAIL_MAX_ENTRIES)
    .optional(),
  artifacts: z
    .array(
      z.object({
        kind: z.enum(['email', 'automation']),
        title: z.string().max(PROMPT_SUGGESTION_TITLE_MAX_CHARS),
      })
    )
    .max(PROMPT_SUGGESTION_ARTIFACTS_MAX)
    .optional(),
})

/**
 * True when the text ends like a finished sentence — the composer skips
 * the suggestion fetch entirely (client gate + server defense in depth).
 * Rare abbreviation false positives suppress a suggestion, which is the
 * safe direction.
 */
export function endsWithTerminalPunctuation(text: string): boolean {
  return /[.!?]\s*$/.test(text)
}

/**
 * Single-line JSON context block for the model. Key order is FIXED and
 * absent fields are omitted so the user-message prefix stays byte-stable
 * between keystrokes within a session — the gateway's implicit prompt
 * caching then shares everything up to the trailing text.
 */
export function buildSuggestionContextLine(
  context: PromptSuggestionContext
): string {
  const ordered: Record<string, unknown> = {}
  ordered.surface = context.surface
  if (context.path) {
    ordered.path = context.path
  }
  if (context.automationId) {
    ordered.viewing = 'automation'
  } else if (context.emailId) {
    ordered.viewing = 'email'
  }
  if (context.brandName) {
    ordered.brand = context.brandName
  }
  if (context.plan) {
    ordered.plan = context.plan
  }
  if (context.creditsLow !== undefined) {
    ordered.creditsLow = context.creditsLow
  }
  if (context.hasVerifiedDomain !== undefined) {
    ordered.hasVerifiedDomain = context.hasVerifiedDomain
  }
  if (context.hasContacts !== undefined) {
    ordered.hasContacts = context.hasContacts
  }
  if (context.hasAutomations !== undefined) {
    ordered.hasAutomations = context.hasAutomations
  }
  if (
    context.activeIntegrationProviders &&
    context.activeIntegrationProviders.length > 0
  ) {
    ordered.activeIntegrations = context.activeIntegrationProviders
  }
  if (context.chatTrail && context.chatTrail.length > 0) {
    ordered.trail = context.chatTrail
  }
  if (context.artifacts && context.artifacts.length > 0) {
    ordered.artifacts = context.artifacts
  }
  return JSON.stringify(ordered)
}
